import api from "api";
import { Spinner } from "components/Spinner/Spinner";
import { SelectOption } from "components/dropdown/Dropdown";
import ProgressBar from "components/helpers/ProgressBar/ProgressBar";
import { endpoints } from "config";
import { Form, Formik, FormikProps } from "formik";
import { DragDropContext, Draggable, DropResult, Droppable } from "react-beautiful-dnd";
import { Dispatch, ReactText, SetStateAction, useEffect, useState } from "react";
import * as yup from "yup";
import MilestoneTaskPicker, { List } from "./Components/MilestoneTaskPicker";
import TriggerTypeSelector from "./Components/TriggerTypeSelector";
import WorkflowItem, { getAllItemActions } from "./Components/WorkflowItem";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import {
  clearSubmitResponseErrors,
  selectSubmitResponse,
  setSubmitResponseErrors,
} from "reducers/submitResponse";
import { parseSubmitResponseEndpointErrors } from "components/Entity/Entity";
import { toast } from "react-toastify";
import ToastMessage, { toastConfig } from "helpers/ToastMessage/ToastMessage";
import capitaliseStringFirstLetter from "helpers/capitaliseStringFirstLetter";
import { ErrorResponse } from "helpers/processErrors";
import { parseCamelCase } from "helpers/parseCamelCase";

import FilterIcon from "assets/Icon_Table_Filter_01.png";
import { otherwise } from "ramda";
import TaskModal from "pages/Gateways/GatewayWizard/sections/BuilderSection/components/TaskModal/TaskModal";
import { WorkflowListData } from "pages/WorkflowConfiguration/WorkflowConfigurations";
import WorkflowGatewayMilestonePage from "./WorkflowGatewayMileStonePage";
import WorkflowGatewayDataCollectionPage from "./WorkflowGatewayDataCollectionPage";
import { Pages } from "../WorkflowCreateEdit";

type AddPageActionType = "create" | "edit" | "view" | "editTask";
type ActionType = "create" | "edit" | "view";

export type UnParsedTasksAndMilestones = {
  name: string;
  id: number;
  type: "task" | "milestone";
}[];

export type WorkflowBuilderErrors = {
  trigger: { [id: number]: string[] };
  actions: { [id: number]: string[] };
  approvals: { [id: number]: string[] };
  approvalOutcomes: { [id: number]: string[] };
  general: string[];
};

export type WorkflowActionTypes = {
  label: string;
  value: any;
  internalCode: string | number | null;
  bEmailNotifications: boolean;
};

enum AddPages {
  "Milestone" = 1,
  "Data" = 2,
}

const deleteMilestone = async (
  addPageActionType: AddPageActionType,
  taskId: number | null,
  setAddPageActionType: React.Dispatch<SetStateAction<AddPageActionType>>,
  actionType: ActionType,
  setShowAddPage: React.Dispatch<SetStateAction<boolean>>
) => {
  if (addPageActionType === "create") {
    let milestonDeleted = false;
    // Call milestone delete endpoint if user clicks cancel on data collection page
    if (taskId) {
      const payload = {
        idsToDelete: [taskId],
      };

      const response = await api.post(endpoints.gateways.deleteTask, payload);

      if (
        response.status === 200 &&
        response.data.status === `1` &&
        response &&
        response.data &&
        response.data.details
      ) {
        milestonDeleted = true;
      } else {
        milestonDeleted = false;
      }

      setAddPageActionType(actionType);
    }

    if (milestonDeleted) {
      toast(
        <ToastMessage
          toastData={[
            {
              title: "Success",
              text: `Deleted unfinished Milestone/task`,
            },
          ]}
          className="ToastSuccess"
        />,
        {
          ...toastConfig,
        }
      );
    } else {
      // Show error
      toast(
        <ToastMessage
          toastData={[
            {
              title: "Error",
              text: `Failed to delete unfinished Milestone/task`,
            },
          ]}
          className="ToastError"
        />,
        {
          ...toastConfig,
        }
      );
    }
  }

  setShowAddPage(false);
  setAddPageActionType(actionType);
};

const createChildElements = (data: any, parsedData: any) => {
  let childElements: any = [];

  if (
    parsedData.childrenWorkflowApprovalOutcomeIDs &&
    Array.isArray(parsedData.childrenWorkflowApprovalOutcomeIDs) &&
    parsedData.childrenWorkflowApprovalOutcomeIDs.length > 0
  ) {
    parsedData.childrenWorkflowApprovalOutcomeIDs.forEach((approvalOutcomeId: number) => {
      let tempChildElementData: any = {};
      if (
        data.workflow.workflowApprovalOutcomes &&
        Array.isArray(data.workflow.workflowApprovalOutcomes)
      ) {
        const approvalOutcomeDataMatches = data.workflow.workflowApprovalOutcomes.filter(
          (item: any) => item.workflowApprovalOutcomesID === approvalOutcomeId
        );
        if (approvalOutcomeDataMatches.length === 1) {
          const approvalOutcomeData = approvalOutcomeDataMatches[0];

          tempChildElementData = { ...approvalOutcomeData };

          let actionData: any = undefined;

          // Actions:
          if (approvalOutcomeData.childWorkflowActionID) {
            if (
              Array.isArray(data.workflow.workflowActions) &&
              data.workflow.workflowActions.length > 0
            ) {
              const actionMatches = data.workflow.workflowActions.filter(
                (item: any) => item.workflowActionsID === approvalOutcomeData.childWorkflowActionID
              );

              if (actionMatches.length === 1) {
                actionData = actionMatches[0];

                let tempChildElements: any = null;

                // Approvals with actions in node:

                let approvalData: any = undefined;
                const allActions = getAllItemActions(
                  { actions: data.workflow.workflowActions },
                  "workflowActionsID",
                  actionData.workflowActionsID
                );

                if (Array.isArray(allActions) && allActions.length > 0) {
                  const lastActionOrderNumber = allActions[allActions.length - 1].orderNumber;

                  if (
                    lastActionOrderNumber &&
                    data.workflow.workflowApprovals &&
                    Array.isArray(data.workflow.workflowApprovals)
                  ) {
                    const approvalMatches = data.workflow.workflowApprovals.filter(
                      (item: any) => item.parentOrderNumber === lastActionOrderNumber
                    );

                    if (Array.isArray(approvalMatches) && approvalMatches.length === 1) {
                      approvalData = approvalMatches[0];

                      let tempChildElementsDataChildElements: any = null;

                      if (approvalData.childrenWorkflowApprovalOutcomeIDs) {
                        tempChildElementsDataChildElements = createChildElements(
                          data,
                          approvalData
                        );
                      }

                      tempChildElements = tempChildElementsDataChildElements;
                    }
                  }
                }

                tempChildElementData = {
                  ...tempChildElementData,
                  ...actionData,
                  ...approvalData,
                  nodeFirstActionId:
                    actionData && actionData.workflowActionsID
                      ? actionData.workflowActionsID
                      : undefined,
                  nodeApprovalId:
                    approvalData && approvalData.workflowApprovalsID
                      ? approvalData.workflowApprovalsID
                      : undefined,
                  childElements: tempChildElements,
                };
              }
            }
          } else if (approvalOutcomeData.childWorkflowApprovalID) {
            // Approvals with no actions in node:
            if (
              Array.isArray(data.workflow.workflowApprovals) &&
              data.workflow.workflowApprovals.length > 0
            ) {
              const approvalMatches = data.workflow.workflowApprovals.filter(
                (item: any) =>
                  item.workflowApprovalsID === approvalOutcomeData.childWorkflowApprovalID
              );

              if (approvalMatches.length === 1) {
                const approvalData = approvalMatches[0];

                let tempChildElementsDataChildElements: any = undefined;

                if (approvalData.childrenWorkflowApprovalOutcomeIDs) {
                  tempChildElementsDataChildElements = createChildElements(data, approvalData);
                }

                tempChildElementData = {
                  ...tempChildElementData,
                  ...approvalData,
                  nodeFirstActionId:
                    actionData && actionData.workflowActionsID
                      ? actionData.workflowActionsID
                      : undefined,
                  nodeApprovalId:
                    approvalData && approvalData.workflowApprovalsID
                      ? approvalData.workflowApprovalsID
                      : undefined,
                  childElements: tempChildElementsDataChildElements,
                };
              }
            }
          }
        }
      }

      childElements.push(tempChildElementData);
    });
  } else {
  }

  return childElements;
};

interface Props {
  closePanel: () => void;
  workflowsId: ReactText | null;
  onDone: () => void;
  currentPageNumber: number;
  totalPages: number;
  actionType: "create" | "edit" | "view";
  originalActionType: "create" | "edit" | "view";
  idFromRowOption: React.ReactText | null;
  submitButtonText: string;
  selectedData: WorkflowListData[];
  currentPage: Pages;
  setCurrentPage: React.Dispatch<SetStateAction<Pages>>;
}

export type ParsedWorkflowItem = {
  parentOrderNumber: number;
  orderNumber: number;
  childElements: ParsedWorkflowItem[] | null;

  workflowTriggerTypesID?: number;
  workflowTriggersID?: number;
  childWorkflowActionID?: number;
  childWorkflowApprovalID?: number;
  workflowApprovalsID?: number;
  childrenWorkflowApprovalOutcomeIDs?: number;
};

const testData: ParsedWorkflowItem = {
  parentOrderNumber: 0,
  orderNumber: 1,
  childElements: [
    {
      parentOrderNumber: 1,
      orderNumber: 1,
      childElements: null,
    },
    {
      parentOrderNumber: 1,
      orderNumber: 2,
      childElements: [
        {
          parentOrderNumber: 2,
          orderNumber: 1,
          childElements: [{ parentOrderNumber: 2, orderNumber: 1, childElements: null }],
        },
        {
          parentOrderNumber: 2,
          orderNumber: 2,
          childElements: null,
        },
      ],
    },
    {
      parentOrderNumber: 1,
      orderNumber: 3,
      childElements: null,
    },
  ],
};

const getAllObjectValuesWithKey = (data: any, keyToFind: string): number[] => {
  let allValues: any = [];
  Object.keys(data).forEach((key: string) => {
    const value = data[key];
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (typeof item === "object" && item.hasOwnProperty(keyToFind)) {
          allValues.push(item[keyToFind]);
        }
      });
    }
  });
  return allValues;
};

const getAllObjectDataWithParentOrderNumberTest = (
  data: any,
  parentOrderNumber: number
): number[] => {
  // let allValues: any = [];
  let allValues: any = {};
  Object.keys(data).forEach((key: string) => {
    const value = data[key];
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (
          typeof item === "object" &&
          item.hasOwnProperty("parentOrderNumber") &&
          item.parentOrderNumber === parentOrderNumber
        ) {
          if (!allValues.hasOwnProperty(key)) {
            allValues[key] = [item];
          } else {
            const tempValues = [...allValues[key], item].sort(
              (a, b) => a.orderNumber - b.orderNumber
            );
            allValues[key] = tempValues;
          }
        }
      });
    }
  });
  return allValues;
};
const getAllObjectDataWithParentOrderNumber = (data: any, parentOrderNumber: number): number[] => {
  let allValues: any = [];
  Object.keys(data).forEach((key: string) => {
    const value = data[key];
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (
          typeof item === "object" &&
          item.hasOwnProperty("parentOrderNumber") &&
          item.parentOrderNumber === parentOrderNumber
        ) {
          allValues.push(item);
        }
      });
    }
  });
  return allValues;
};

const getObjectOrderNumbers = (data: any): number[] => {
  let allValues: any = [];

  data.forEach((item: any) => {
    if (typeof item === "object" && item.hasOwnProperty("orderNumber")) {
      allValues.push(item.orderNumber);
    }
  });

  return allValues;
};

const checkChildElementsOrderNumber = (data: any, parentOrderNumber: number, dataToAdd: any) => {
  let newData = { ...data };

  let newChildElements: any[] = [];

  if (
    typeof data === "object" &&
    data.hasOwnProperty("orderNumber") &&
    data.orderNumber === parentOrderNumber
  ) {
    newData.childElements = dataToAdd;
  } else {
    if (
      data &&
      typeof data === "object" &&
      data.hasOwnProperty("childElements") &&
      Array.isArray(data.childElements)
    ) {
      data.childElements.forEach((item: any) => {
        if (
          typeof item === "object" &&
          item.hasOwnProperty("orderNumber") &&
          item.orderNumber === parentOrderNumber
        ) {
          newChildElements.push(dataToAdd);
        } else {
          newChildElements.push(checkChildElementsOrderNumber(data, parentOrderNumber, dataToAdd));
        }
      });
    }
  }

  if (newChildElements) {
    newData.childElements = newChildElements;
  }

  return newData;
};

const updateChildElementsBasedOnOrderNumber = (
  data: any,
  parentOrderNumber: number,
  dataToAdd: any
) => {
  let newData = { ...data };

  let newChildElements: any[] = data.childElements ? [...data.childElements] : [];

  if (
    typeof data === "object" &&
    data.hasOwnProperty("orderNumber") &&
    `${data.orderNumber}` === `${parentOrderNumber}`
  ) {
    newChildElements.push(dataToAdd);
  } else {
    if (
      data &&
      typeof data === "object" &&
      data.hasOwnProperty("childElements") &&
      Array.isArray(data.childElements)
    ) {
      data.childElements.forEach((item: any) => {
        newChildElements.push(checkChildElementsOrderNumber(item, parentOrderNumber, dataToAdd));
      });
    }
  }

  if (newChildElements) {
    newData.childElements = newChildElements;
  }

  return newData;
};

const addDataToObjectBasedOnParentOrderNumber = (
  data: any,
  parentOrderNumber: number,
  dataToAdd: any
) => {
  return checkChildElementsOrderNumber(data, parentOrderNumber, dataToAdd);
};

export enum WorkflowReferenceDataNames {
  "TRIGGER" = "trigger",
  "ACTIONS" = "actions",
  "APPROVALS" = "approvals",
  "APPROVAL_OUTCOMES" = "approvalOutcomes",
  "ACTION_TYPES" = "actionTypes",
  "TRIGGER_TYPES" = "triggerTypes",
}

export type ReferenceData = Partial<{
  [referenceDataType in WorkflowReferenceDataNames]: any | null;
}>;

const parseSelectList = (options: { id: number | string; name: string }[]) =>
  options ? options.map((option) => ({ value: option.id, label: option.name })) : [];

const parseActionTypes = (
  options: {
    id: number | string;
    name: string;
    internalCode: string;
    bEmailNotifications: boolean;
  }[]
): WorkflowActionTypes[] =>
  options
    ? options.map((option) => ({
        value: option.id,
        label: option.name,
        internalCode: option.internalCode,
        bEmailNotifications: option.bEmailNotifications,
      }))
    : [];

const WorkflowBuilderPage: React.FC<Props> = ({
  closePanel,
  workflowsId,
  onDone,
  currentPageNumber,
  totalPages,
  actionType,
  idFromRowOption,
  submitButtonText,
  selectedData,
  currentPage,
  setCurrentPage,
  originalActionType,
}) => {
  const [initialValues, setInitialValues] = useState<ParsedWorkflowItem | null>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [templateGateways, setTempleGateways] = useState<SelectOption[]>([]);
  const [workflowTriggerTypes, setWorkflowTriggerTypes] = useState<SelectOption[]>([]);
  const [documentTypes, setDocumentTypes] = useState<SelectOption[]>([]);
  const [surveyTemplateOptions, setSurveyTemplateOptions] = useState<SelectOption[]>([]);
  const [projectTypes, setProjectTypes] = useState<SelectOption[]>([]);
  const [triggerDatesOptions, setTriggerDatesOptions] = useState<SelectOption[]>([]);
  const [userPools, setUserPools] = useState<SelectOption[]>([]);
  const [contactTypes, setContactTypes] = useState<SelectOption[]>([]);
  const [workflowActionTypes, setWorkflowActionTypes] = useState<WorkflowActionTypes[]>([]);
  const [workflows, setWorkflows] = useState<SelectOption[]>([]);

  const [parsedData, setParsedData] = useState<ParsedWorkflowItem | null>(testData);

  const [referenceData, setReferenceData] = useState<ReferenceData | null>(null);

  const [taskPickerMimised, setTaskPickerMimised] = useState<boolean>(false);

  const [tempTriggerMilestoneData, setTempTriggerMilestoneData] = useState<List | null>(null);
  const [tempTriggerMilestoneId, setTempTriggerMilestoneId] = useState<number | null>(null);
  const [tempActionMilestoneData, setTempActionMilestoneData] = useState<List | null>(null);
  const [tempActionMilestoneId, setTempActionMilestoneId] = useState<number | null>(null);
  const [tempActionDestinationMilestoneId, setTempActionDestinationMilestoneId] = useState<
    number | null
  >(null);

  const [milestonestasksList, setMilestonestasksList] = useState<List[] | null>(null);

  const [getEndpointError, setGetEndpointError] = useState<boolean>(false);
  const [postEndpointError, setPostEndpointError] = useState<string[] | null>(null);

  const [parsedSubmitResponseErrors, setParsedSubmitResponseErrors] = useState<any | null>(null);

  const [allTasksOriginalData, setAllTasksOriginalData] = useState<any>(null);

  useEffect(() => {
    setParsedData(initialValues);
  }, [initialValues]);

  const iterate = (obj: any, referenceData: any) => {
    let newObj = { ...obj };
    if (obj.hasOwnProperty("orderNumber") && referenceData.hasOwnProperty(`${obj.orderNumber}`)) {
      newObj.childElements = referenceData[`${obj.orderNumber}`];
    }

    if (newObj.childElements && Array.isArray(newObj.childElements)) {
      newObj.childElements.forEach((item: any, idx: number) => {
        newObj.childElements[idx] = iterate(item, referenceData);
      });
    }

    return newObj;
  };

  const parseBuilderData = (data: any) => {
    if (
      data &&
      typeof data === "object" &&
      data.workflowActionTypes &&
      data.workflowTriggerTypes &&
      data.workflow &&
      typeof data.workflow === "object"
    ) {
      let parsedData = {
        parentOrderNumber: 0,
        orderNumber: 1,
        childElements: [],

        workflowTriggerTypesID: null,
        workflowTriggersID: null,
        childWorkflowActionID: null,
        childWorkflowApprovalID: null,
        workflowApprovalsID: null,
        childrenWorkflowApprovalOutcomeIDs: null,
      };

      const trigger = data.workflow?.workflowTrigger;
      const actions = data.workflow?.workflowActions;
      let approvals = data.workflow?.workflowApprovals;

      if (approvals && Array.isArray(approvals) && approvals.length > 0) {
        approvals.forEach((item: any, idx: number) => {
          if (item.contactTypesIDs) {
            approvals[idx].approvalContactTypesIDs = item.contactTypesIDs;
            delete approvals[idx].contactTypesIDs;
          }
          if (item.userPoolsID) {
            approvals[idx].approvalUserPoolsID = item.userPoolsID;
            delete approvals[idx].userPoolsID;
          }
        });
      }

      const approvalOutcomes = data.workflow?.workflowApprovalOutcomes;
      const orderNumber = data.workflow?.workflowsID;

      const actionTypes = data.workflowActionTypes;
      const triggerTypes = data.workflowTriggerTypes;

      const allParentOrderNumbers = [
        ...new Set(getAllObjectValuesWithKey(data.workflow, "parentOrderNumber")),
      ].sort((a: number, b: number) => {
        return a - b;
      });

      // Create parsed data (parse endpoint data into nested format for display and CRUD):

      let approvalOutcomesOrderNumberData: { [orderNumber: number]: any } = {};

      if (
        data &&
        data.workflow &&
        data.workflow.workflowApprovalOutcomes &&
        Array.isArray(data.workflow.workflowApprovalOutcomes)
      ) {
        data.workflow.workflowApprovalOutcomes.forEach((item: any) => {
          if (approvalOutcomesOrderNumberData.hasOwnProperty(item.parentOrderNumber)) {
            approvalOutcomesOrderNumberData[item.parentOrderNumber] = [
              ...approvalOutcomesOrderNumberData[item.parentOrderNumber],
              item,
            ];
          } else {
            approvalOutcomesOrderNumberData[item.parentOrderNumber] = [item];
          }
        });
      }

      let approvalOutcomesIdData: { [orderNumber: number]: any } = {};

      if (
        data &&
        data.workflow &&
        data.workflow.workflowApprovalOutcomes &&
        Array.isArray(data.workflow.workflowApprovalOutcomes)
      ) {
        data.workflow.workflowApprovalOutcomes.forEach((item: any) => {
          approvalOutcomesIdData[item.workflowApprovalOutcomesID] = item;
        });
      }

      let approvalOrderNumberData: { [orderNumber: number]: any } = {};
      let allApprovals: [];

      if (
        data &&
        data.workflow &&
        data.workflow.workflowApprovals &&
        Array.isArray(data.workflow.workflowApprovals)
      ) {
        data.workflow.workflowApprovals.forEach((item: any) => {
          approvalOrderNumberData[item.parentOrderNumber] = item;
        });
      }
      let approvalIdData: { [orderNumber: number]: any } = {};

      if (
        data &&
        data.workflow &&
        data.workflow.workflowApprovals &&
        Array.isArray(data.workflow.workflowApprovals)
      ) {
        data.workflow.workflowApprovals.forEach((item: any) => {
          approvalIdData[item.workflowApprovalsID] = item;
        });
      }

      let actionOrderNumberData: { [orderNumber: number]: any } = {};
      if (
        data &&
        data.workflow &&
        data.workflow.workflowActions &&
        Array.isArray(data.workflow.workflowActions)
      ) {
        data.workflow.workflowActions.forEach((item: any) => {
          actionOrderNumberData[item.parentOrderNumber] = item;
        });
      }

      let actionIdData: { [orderNumber: number]: any } = {};
      if (
        data &&
        data.workflow &&
        data.workflow.workflowActions &&
        Array.isArray(data.workflow.workflowActions)
      ) {
        data.workflow.workflowActions.forEach((item: any) => {
          actionIdData[item.workflowActionsID] = item;
        });
      }

      let testParsedData3: any = {};

      // Add trigger data:
      if (
        data &&
        data.workflow &&
        data.workflow.workflowTrigger &&
        typeof data.workflow.workflowTrigger === "object"
      ) {
        testParsedData3 = { ...data.workflow.workflowTrigger };
      }

      let firstChildItem: "action" | "approval" | null = null;
      if (testParsedData3.childWorkflowActionID) {
        firstChildItem = "action";
      } else if (testParsedData3.childWorkflowApprovalID) {
        firstChildItem = "approval";
      }

      if (
        firstChildItem === "action" &&
        data.workflow.workflowActions &&
        Array.isArray(data.workflow.workflowActions)
      ) {
        const actionDataMatches = data.workflow.workflowActions.filter(
          (item: any) => item.workflowActionsID === testParsedData3.childWorkflowActionID
        );

        if (actionDataMatches.length === 1) {
          const actionData = actionDataMatches[0];

          const allActions = getAllItemActions(
            { actions: data.workflow.workflowActions },
            "workflowActionsID",
            actionData.workflowActionsID
          );

          let approvalData: any = {};

          if (Array.isArray(allActions) && allActions.length > 0) {
            const lastActionOrderNumber = allActions[allActions.length - 1].orderNumber;

            if (
              typeof lastActionOrderNumber === "number" &&
              data.workflow.workflowApprovals &&
              Array.isArray(data.workflow.workflowApprovals)
            ) {
              const approvalMatches = data.workflow.workflowApprovals.filter(
                (item: any) => item.parentOrderNumber === lastActionOrderNumber
              );

              if (Array.isArray(approvalMatches) && approvalMatches.length === 1) {
                approvalData = approvalMatches[0];
              }
            }
          }

          testParsedData3 = {
            ...testParsedData3,
            nodeFirstActionId:
              actionData && actionData.workflowActionsID ? actionData.workflowActionsID : undefined,
            nodeApprovalId:
              approvalData && approvalData.workflowApprovalsID
                ? approvalData.workflowApprovalsID
                : undefined,
            ...approvalData,
          };
        }
      } else if (
        firstChildItem === "approval" &&
        data.workflow.workflowApprovals &&
        Array.isArray(data.workflow.workflowApprovals)
      ) {
        const approvalDataMatches = data.workflow.workflowApprovals.filter(
          (item: any) => item.workflowApprovalsID === testParsedData3.childWorkflowApprovalID
        );

        if (approvalDataMatches.length === 1) {
          const approvalData = approvalDataMatches[0];

          testParsedData3 = {
            ...testParsedData3,
            nodeFirstActionId: null,
            nodeApprovalId:
              approvalData && approvalData.workflowApprovalsID
                ? approvalData.workflowApprovalsID
                : undefined,
            ...approvalData,
          };
        }
      }

      const childElements = createChildElements(data, testParsedData3);

      testParsedData3.childElements = childElements;

      let parentOrderNumberData: { [parentOrderNumber: number]: any } = {};

      allParentOrderNumbers.forEach((parentOrderNumber: number) => {
        parentOrderNumberData[parentOrderNumber] = getAllObjectDataWithParentOrderNumber(
          data.workflow,
          parentOrderNumber
        );
      });

      if (trigger && typeof trigger === "object") {
        parsedData.orderNumber = trigger?.orderNumber;
        parsedData.workflowTriggerTypesID = trigger?.workflowTriggerTypesID;
        parsedData.workflowTriggersID = trigger?.workflowTriggersID;
        parsedData.childWorkflowActionID = trigger?.childWorkflowActionID;
        parsedData.childWorkflowApprovalID = trigger?.childWorkflowApprovalID;
        parsedData.workflowApprovalsID = trigger?.workflowApprovalsID;
        parsedData.childrenWorkflowApprovalOutcomeIDs = trigger?.childrenWorkflowApprovalOutcomeIDs;
      }

      let allTasksMilestones: List[] | null = null;

      if (data.allTasks) {
        try {
          allTasksMilestones = data.allTasks.map((item: any) => {
            return { tasksName: item.name, tasksId: item.id };
          });
        } catch {
          console.log("Could not parse all tasks data");
        }
      }

      return {
        parsedData: testParsedData3 ?? {},
        trigger: trigger ?? {},
        actions: actions ?? [],
        approvals: approvals ?? [],
        approvalOutcomes: approvalOutcomes ?? [],
        actionTypes: actionTypes ?? [],
        triggerTypes: triggerTypes ?? [],
        allTasksMilestones: allTasksMilestones ?? [],
        allTasks: data?.allTasks ?? [],
      };
    } else {
      return {
        parsedData: null,
        trigger: null,
        actions: null,
        approvals: null,
        approvalOutcomes: null,
        actionTypes: null,
        triggerTypes: null,
        allTasksMilestones: null,
        allTasks: null,
      };
    }
  };

  const fetchBuilderInfo = async (onlyTaskData: boolean = false) => {
    let params = workflowsId ? { params: { Id: workflowsId } } : null;

    try {
      const res = await api.get(endpoints.workflowConfiguration.builder, params ?? {});
      if (res.status === 200 && res.data.status === "1") {
        if (res.data.details) {
          const {
            parsedData,
            trigger,
            actions,
            approvals,
            approvalOutcomes,
            actionTypes,
            triggerTypes,
            allTasksMilestones,
            allTasks,
          } = parseBuilderData(res.data.details);

          const tempReferenceData = {
            trigger,
            actions,
            approvals,
            approvalOutcomes,
            actionTypes,
            triggerTypes,
          };

          if (trigger && trigger.milestonesID) {
            setTempTriggerMilestoneId(trigger.milestonesID);

            if (allTasks) {
              const match = allTasks.filter((item: any) => item.id === trigger.milestonesID);

              if (match.length === 1) {
                setTempTriggerMilestoneData({
                  tasksName: match[0].name,
                  tasksId: match[0].id,
                });
              }
            }
          } else {
            setTempTriggerMilestoneId(null);
          }

          if (onlyTaskData) {
            setAllTasksOriginalData(allTasks);
          } else {
            setAllTasksOriginalData(allTasks);
            setReferenceData(tempReferenceData);

            const filterKey = "tasksId";

            const parsedAllTasksMilestones = Array.isArray(allTasksMilestones)
              ? [...new Map(allTasksMilestones.map((item) => [item[filterKey], item])).values()]
              : null;

            setMilestonestasksList(parsedAllTasksMilestones);

            const parsedDatav2 = updateParsedDataTriggerData(tempReferenceData, parsedData);

            setParsedData(parsedDatav2);

            setInitialValues(parsedDatav2);

            if (res.data.details.templateGateways) {
              setTempleGateways(parseSelectList(res.data.details.templateGateways));
            }
            if (res.data.details.workflowTriggerTypes) {
              setWorkflowTriggerTypes(parseSelectList(res.data.details.workflowTriggerTypes));
            }
            if (res.data.details.documentTypes) {
              setDocumentTypes(parseSelectList(res.data.details.documentTypes));
            }
            if (res.data.details.surveyTemplates) {
              setSurveyTemplateOptions(parseSelectList(res.data.details.surveyTemplates));
            }
            if (res.data.details.projectTypes) {
              setProjectTypes(parseSelectList(res.data.details.projectTypes));
            }
            if (res.data.details.dateTypes) {
              setTriggerDatesOptions(res.data.details.dateTypes);
            }
            if (res.data.details.userPools) {
              setUserPools(parseSelectList(res.data.details.userPools));
            }
            if (res.data.details.contactTypes) {
              setContactTypes(parseSelectList(res.data.details.contactTypes));
            }
            if (res.data.details.workflowActionTypes) {
              setWorkflowActionTypes(parseActionTypes(res.data.details.workflowActionTypes));
            }
            if (res.data.details.workflows) {
              setWorkflows(parseSelectList(res.data.details.workflows));
            }
          }

          setGetEndpointError(false);
          setLoading(false);
        }
      }
    } catch (error) {
      console.log(error);

      setGetEndpointError(true);
    }
  };

  useEffect(() => {
    setLoading(true);
    if (workflowsId) {
      fetchBuilderInfo();
    } else {
      setLoading(false);
    }
  }, [workflowsId]);

  const updateParsedDataTriggerData = (referenceData: any, parsedData: any) => {
    // Update parent parsed data item to be up to date with trigger reference data:

    const newParsedData = _.cloneDeep(parsedData) ?? {};
    if (
      referenceData &&
      referenceData[WorkflowReferenceDataNames.TRIGGER] &&
      typeof referenceData[WorkflowReferenceDataNames.TRIGGER] === "object" &&
      parsedData
    ) {
      Object.keys(referenceData[WorkflowReferenceDataNames.TRIGGER]).forEach((key: string) => {
        const value = referenceData[WorkflowReferenceDataNames.TRIGGER][key];

        newParsedData[key as keyof typeof newParsedData] = value;
      });
    } else {
      // Blank new parsedData for create action type or newly created workflow where the builder has not been saved before:
      if (referenceData && referenceData[WorkflowReferenceDataNames.TRIGGER]) {
      }
    }

    if (newParsedData !== parsedData) {
      setParsedData(newParsedData);
    }

    // Override orderNumber as 1:
    newParsedData.parentOrderNumber = -1;

    return newParsedData;
  };

  const handleSubmit = () => {};

  const onDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;

    if (
      draggableId &&
      typeof draggableId === "string" &&
      destination &&
      destination.droppableId &&
      typeof destination.droppableId === "string"
    ) {
      const splitId = draggableId.split("-");

      const splitDestination = destination.droppableId.split("-");

      const destinationType = splitDestination[1];

      const destinationId = splitDestination[splitDestination.length - 1];

      const id = splitId[splitId.length - 1];

      if (destinationType === "milestoneUpdateTrigger") {
        setTempTriggerMilestoneId(parseInt(id));
      } else if (destinationType === "milestoneUpdateAction") {
        setTempActionMilestoneId(parseInt(id));
        setTempActionDestinationMilestoneId(parseInt(destinationId));
      }
    }
  };

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [validating, setValidating] = useState<boolean>(false);
  const [formValid, setFormValid] = useState<boolean>(false);
  const [workflowBuilderErrors, setWorkflowBuilderErrors] = useState<WorkflowBuilderErrors>({
    trigger: {},
    actions: {},
    approvals: {},
    approvalOutcomes: {},
    general: [],
  });

  const startSubmit = () => {
    setValidating(true);

    let tempWorkflowBuilderErrors: WorkflowBuilderErrors = {
      trigger: {},
      actions: {},
      approvals: {},
      approvalOutcomes: {},
      general: [],
    };

    // Trigger Error validation:
    if (referenceData && referenceData.trigger) {
      const triggerData = referenceData?.trigger;

      if (typeof triggerData.workflowTriggerTypesID === "number") {
        let triggerTypeLabel = null;

        if (
          workflowTriggerTypes &&
          workflowTriggerTypes.length > 0 &&
          typeof triggerData.workflowTriggerTypesID === "number"
        ) {
          const match = workflowTriggerTypes.filter(
            (item) => item.value === triggerData.workflowTriggerTypesID
          );

          if (match && match.length === 1 && match[0].label && typeof match[0].label === "string") {
            triggerTypeLabel = match[0].label.toLowerCase().trim();
          }
        }

        // Document upload:
        if (triggerTypeLabel === "document upload") {
          // Document type required:
          if (!triggerData.documentTypesID) {
            if (tempWorkflowBuilderErrors.trigger.hasOwnProperty(0)) {
              tempWorkflowBuilderErrors.trigger[0] = [
                ...tempWorkflowBuilderErrors.trigger[0],
                "Please select a document type",
              ];
            } else {
              tempWorkflowBuilderErrors.trigger[0] = ["Please select a document type"];
            }
          }

          // One or more project types required:
          if (
            !triggerData.projectTypesIDs ||
            (triggerData.projectTypesIDs && triggerData.projectTypesIDs.length < 1)
          ) {
            if (tempWorkflowBuilderErrors.trigger.hasOwnProperty(0)) {
              tempWorkflowBuilderErrors.trigger[0] = [
                ...tempWorkflowBuilderErrors.trigger[0],
                "Please select one or more project types",
              ];
            } else {
              tempWorkflowBuilderErrors.trigger[0] = ["Please select one or more project types"];
            }
          }
        }

        // Document request:
        if (triggerTypeLabel === "document request") {
          // Document type required:
          if (!triggerData.documentTypesID) {
            if (tempWorkflowBuilderErrors.trigger.hasOwnProperty(0)) {
              tempWorkflowBuilderErrors.trigger[0] = [
                ...tempWorkflowBuilderErrors.trigger[0],
                "Please select a document type",
              ];
            } else {
              tempWorkflowBuilderErrors.trigger[0] = ["Please select a document type"];
            }
          }

          // One or more contact types required AND/OR one user pool required:
          let contactTypeValid = true;
          let userpoolValid = true;

          if (
            !triggerData.contactTypesIDs ||
            (triggerData.contactTypesIDs && triggerData.contactTypesIDs.length < 1)
          ) {
            contactTypeValid = false;
          }

          if (!triggerData.userPoolsID) {
            userpoolValid = false;
          }

          if (!contactTypeValid && !userpoolValid) {
            if (tempWorkflowBuilderErrors.trigger.hasOwnProperty(0)) {
              tempWorkflowBuilderErrors.trigger[0] = [
                ...tempWorkflowBuilderErrors.trigger[0],
                "Please select one or more contact types and/or one user pool",
              ];
            } else {
              tempWorkflowBuilderErrors.trigger[0] = [
                "Please select one or more contact types and/or one user pool",
              ];
            }
          }
        }

        // Milestone update:
        if (triggerTypeLabel === "milestone update") {
          // Milestone required:
          if (!triggerData.milestonesID) {
            if (tempWorkflowBuilderErrors.trigger.hasOwnProperty(0)) {
              tempWorkflowBuilderErrors.trigger[0] = [
                ...tempWorkflowBuilderErrors.trigger[0],
                "Please select a milestone",
              ];
            } else {
              tempWorkflowBuilderErrors.trigger[0] = ["Please select a milestone"];
            }
          }

          // Project type(s) optional:

          // One or more contact types required AND/OR one user pool required:
          let contactTypeValid = true;
          let userpoolValid = true;

          if (!triggerData.triggerDates) {
            contactTypeValid = false;
          }

          // Unsure if this needs changing as it's a boolean and can be null or false as well as true
          if (!triggerData.bTriggeredByCompletingMilestone) {
            userpoolValid = false;
          }

          if (!contactTypeValid && !userpoolValid) {
            if (tempWorkflowBuilderErrors.trigger.hasOwnProperty(0)) {
              tempWorkflowBuilderErrors.trigger[0] = [
                ...tempWorkflowBuilderErrors.trigger[0],
                "Please select a trigger date or select triggered by completing milestone",
              ];
            } else {
              tempWorkflowBuilderErrors.trigger[0] = [
                "Please select a trigger date or select triggered by completing milestone",
              ];
            }
          }
        }

        //New communication:
        if (triggerTypeLabel === "new communication") {
          if (
            !triggerData.contactTypesIDs ||
            (triggerData.contactTypesIDs && triggerData.contactTypesIDs.length < 1)
          ) {
            if (tempWorkflowBuilderErrors.trigger.hasOwnProperty(0)) {
              tempWorkflowBuilderErrors.trigger[0] = [
                ...tempWorkflowBuilderErrors.trigger[0],
                "Please select one or more contact types",
              ];
            } else {
              tempWorkflowBuilderErrors.trigger[0] = ["Please select one or more contact types"];
            }
          }
        }
      } else {
        if (tempWorkflowBuilderErrors.trigger.hasOwnProperty(0)) {
          tempWorkflowBuilderErrors.trigger[0] = [
            ...tempWorkflowBuilderErrors.trigger[0],
            "Please select a trigger type",
          ];
        } else {
          tempWorkflowBuilderErrors.trigger[0] = ["Please select a trigger type"];
        }
      }
    } else {
      if (tempWorkflowBuilderErrors.trigger.hasOwnProperty(0)) {
        tempWorkflowBuilderErrors.trigger[0] = [
          ...tempWorkflowBuilderErrors.trigger[0],
          "Please select a trigger type",
        ];
      } else {
        tempWorkflowBuilderErrors.trigger[0] = ["Please select a trigger type"];
      }
    }

    // Actions Error validation:
    if (referenceData && referenceData.actions && Array.isArray(referenceData.actions)) {
      referenceData.actions.map((action: any) => {
        const workflowId = action.workflowActionsID;

        let actionTypeInternalCode = null;

        if (
          workflowActionTypes &&
          workflowActionTypes.length > 0 &&
          typeof action.workflowActionTypesID === "number"
        ) {
          const match = workflowActionTypes.filter(
            (item) => item.value === action.workflowActionTypesID
          );

          if (match && match.length === 1 && match[0].label && typeof match[0].label === "string") {
            actionTypeInternalCode = match[0].internalCode;
          }
        }

        if (!action.workflowActionTypesID) {
          if (tempWorkflowBuilderErrors.actions.hasOwnProperty(workflowId)) {
            tempWorkflowBuilderErrors.actions[workflowId] = [
              ...tempWorkflowBuilderErrors.actions[workflowId],
              "Please select an action type",
            ];
          } else {
            tempWorkflowBuilderErrors.actions[workflowId] = ["Please select a trigger type"];
          }
        } else {
          // Update milestone/task
          if (actionTypeInternalCode && actionTypeInternalCode === "UPDATE_MILESTONE_OR_TASK") {
            // Milestone required:

            if (!action.updateTasksID) {
              if (tempWorkflowBuilderErrors.actions.hasOwnProperty(workflowId)) {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  ...tempWorkflowBuilderErrors.actions[workflowId],
                  "Please select a milestone",
                ];
              } else {
                tempWorkflowBuilderErrors.actions[workflowId] = ["Please select a milestone"];
              }
            }

            // Date to update optional:

            // Override flag
          }

          // Workflow action:
          if (actionTypeInternalCode && actionTypeInternalCode === "WORKFLOW_ACTION") {
            // One or more contact types and/or one user pool required:

            let contactTypeValid = true;
            let userpoolValid = true;

            if (
              !action.contactTypesIDs ||
              (action.contactTypesIDs && action.contactTypesIDs.length < 1)
            ) {
              contactTypeValid = false;
            }

            if (!action.userPoolsID) {
              userpoolValid = false;
            }

            if (!contactTypeValid && !userpoolValid) {
              if (tempWorkflowBuilderErrors.actions.hasOwnProperty(workflowId)) {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  ...tempWorkflowBuilderErrors.actions[workflowId],
                  "Please select one or more contact types and/or one user pool",
                ];
              } else {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  "Please select one or more contact types and/or one user pool",
                ];
              }
            }

            // Document type optional

            // Message text required:
            if (!action.messageText) {
              if (tempWorkflowBuilderErrors.actions.hasOwnProperty(workflowId)) {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  ...tempWorkflowBuilderErrors.actions[workflowId],
                  "Please provide a message",
                ];
              } else {
                tempWorkflowBuilderErrors.actions[workflowId] = ["Please provide a message"];
              }
            }
          }

          // Workflow notifications:
          if (actionTypeInternalCode && actionTypeInternalCode === "WORKFLOW_NOTIFICATION") {
            // One or more contact types and/or one user pool required:

            let contactTypeValid = true;
            let userpoolValid = true;

            if (
              !action.contactTypesIDs ||
              (action.contactTypesIDs && action.contactTypesIDs.length < 1)
            ) {
              contactTypeValid = false;
            }

            if (!action.userPoolsID) {
              userpoolValid = false;
            }

            if (!contactTypeValid && !userpoolValid) {
              if (tempWorkflowBuilderErrors.actions.hasOwnProperty(workflowId)) {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  ...tempWorkflowBuilderErrors.actions[workflowId],
                  "Please select one or more contact types and/or one user pool",
                ];
              } else {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  "Please select one or more contact types and/or one user pool",
                ];
              }
            }

            // Message text flag:
          }

          // Calculated date
          if (actionTypeInternalCode && actionTypeInternalCode === "CALCULATED_DATE") {
            // Milestone/task to update required:
            if (!action.updateTasksID) {
              if (tempWorkflowBuilderErrors.actions.hasOwnProperty(workflowId)) {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  ...tempWorkflowBuilderErrors.actions[workflowId],
                  "Please select a Milestone/task to update",
                ];
              } else {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  "Please select a Milestone/task to update",
                ];
              }
            }

            // Milestone/task to base data changes on required:
            if (!action.baseOnTasksID) {
              if (tempWorkflowBuilderErrors.actions.hasOwnProperty(workflowId)) {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  ...tempWorkflowBuilderErrors.actions[workflowId],
                  "Please select a Milestone/task to base data changes on",
                ];
              } else {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  "Please select a Milestone/task to base data changes on",
                ];
              }
            }

            let parsedTaskDayChange = null;

            // Check if taskDayChange is a number, if not set to 0
            if (
              action.taskDayChange !== undefined &&
              action.taskDayChange !== null &&
              typeof action.taskDayChange === "number"
            ) {
              parsedTaskDayChange = action.taskDayChange;
            } else {
              action.taskDayChange = "0";
              parsedTaskDayChange = action.taskDayChange;
            }

            // Days to add/subtract (unsure if this required):
            if (!parsedTaskDayChange && parsedTaskDayChange !== 0) {
              if (tempWorkflowBuilderErrors.actions.hasOwnProperty(workflowId)) {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  ...tempWorkflowBuilderErrors.actions[workflowId],
                  "Please provide Days to add/subtract",
                ];
              } else {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  "Please provide Days to add/subtract",
                ];
              }
            } else if (
              typeof parsedTaskDayChange === "number" &&
              !Number.isInteger(parsedTaskDayChange)
              // parsedTaskDayChange < 0
            ) {
              if (tempWorkflowBuilderErrors.actions.hasOwnProperty(workflowId)) {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  ...tempWorkflowBuilderErrors.actions[workflowId],
                  "Please provide Days to add/subtract that are integers",
                ];
              } else {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  "Please provide Days to add/subtract that are integers",
                ];
              }
            }

            // Business or Calendar choice required:
            if (!action.hasOwnProperty("bBusinessDaysOnly")) {
              if (tempWorkflowBuilderErrors.actions.hasOwnProperty(workflowId)) {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  ...tempWorkflowBuilderErrors.actions[workflowId],
                  "Please provide Business or calendar days choice",
                ];
              } else {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  "Please provide Business or calendar days choice",
                ];
              }
            }

            // Date to update required:
            if (!action.updateDate) {
              if (tempWorkflowBuilderErrors.actions.hasOwnProperty(workflowId)) {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  ...tempWorkflowBuilderErrors.actions[workflowId],
                  "Please select a Date to update",
                ];
              } else {
                tempWorkflowBuilderErrors.actions[workflowId] = ["Please select a Date to update"];
              }
            }

            // Date to base data changes on required:
            if (!action.baseOnDate) {
              if (tempWorkflowBuilderErrors.actions.hasOwnProperty(workflowId)) {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  ...tempWorkflowBuilderErrors.actions[workflowId],
                  "Please select a Date to base data changes on",
                ];
              } else {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  "Please select a Date to base data changes on",
                ];
              }
            }

            // Exclude holidays choice required:
            if (!action.hasOwnProperty("bExcludeHolidays")) {
              if (tempWorkflowBuilderErrors.actions.hasOwnProperty(workflowId)) {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  ...tempWorkflowBuilderErrors.actions[workflowId],
                  "Please provide if you want to Exclude holidays",
                ];
              } else {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  "Please provide if you want to Exclude holidays",
                ];
              }
            }
          }

          // Trigger workflow:
          if (actionTypeInternalCode && actionTypeInternalCode === "TRIGGER_WORKFLOW") {
            let workflowValid = true;

            if (
              !action.hasOwnProperty("workflowToTriggersID") ||
              typeof action.workflowToTriggersID !== "number"
            ) {
              workflowValid = false;
            }

            if (!workflowValid) {
              if (tempWorkflowBuilderErrors.actions.hasOwnProperty(workflowId)) {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  ...tempWorkflowBuilderErrors.actions[workflowId],
                  "Please select a workflow",
                ];
              } else {
                tempWorkflowBuilderErrors.actions[workflowId] = ["Please select a workflow"];
              }
            }

            // Message text flag:
          }

          // Reject survey:
          if (actionTypeInternalCode && actionTypeInternalCode === "REJECT_SURVEY") {
            let messageTextValid = true;

            if (
              !action.hasOwnProperty("messageText") ||
              typeof action.messageText !== "string" ||
              (typeof action.messageText === "string" && action.messageText.trim() === "")
            ) {
              messageTextValid = false;
            }

            if (!messageTextValid) {
              if (tempWorkflowBuilderErrors.actions.hasOwnProperty(workflowId)) {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  ...tempWorkflowBuilderErrors.actions[workflowId],
                  "Please provide a message",
                ];
              } else {
                tempWorkflowBuilderErrors.actions[workflowId] = ["Please provide a message"];
              }
            }

            // Message text flag:
          }

          // Advanced calculated date
          if (actionTypeInternalCode && actionTypeInternalCode === "ADVANCED_CALCULATED_DATE") {
            // Milestone/task to update required:
            if (!action.updateTasksID) {
              if (tempWorkflowBuilderErrors.actions.hasOwnProperty(workflowId)) {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  ...tempWorkflowBuilderErrors.actions[workflowId],
                  "Please select a Milestone/task to update",
                ];
              } else {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  "Please select a Milestone/task to update",
                ];
              }
            }

            // Milestone/task to base data changes on required:
            if (!action.baseOnTasksID) {
              if (tempWorkflowBuilderErrors.actions.hasOwnProperty(workflowId)) {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  ...tempWorkflowBuilderErrors.actions[workflowId],
                  "Please select a Milestone/task to base data changes on",
                ];
              } else {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  "Please select a Milestone/task to base data changes on",
                ];
              }
            }

            let parsedTaskDayChange = null;

            if (action.taskDayChange && typeof action.taskDayChange === "number") {
              parsedTaskDayChange = action.taskDayChange;
            }

            // Business or Calendar choice required:
            if (!action.hasOwnProperty("bBusinessDaysOnly")) {
              if (tempWorkflowBuilderErrors.actions.hasOwnProperty(workflowId)) {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  ...tempWorkflowBuilderErrors.actions[workflowId],
                  "Please provide Business or calendar days choice",
                ];
              } else {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  "Please provide Business or calendar days choice",
                ];
              }
            }

            // Date to update required:
            if (!action.updateDate) {
              if (tempWorkflowBuilderErrors.actions.hasOwnProperty(workflowId)) {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  ...tempWorkflowBuilderErrors.actions[workflowId],
                  "Please select a Date to update",
                ];
              } else {
                tempWorkflowBuilderErrors.actions[workflowId] = ["Please select a Date to update"];
              }
            }

            // Date to base data changes on required:
            if (!action.baseOnDate) {
              if (tempWorkflowBuilderErrors.actions.hasOwnProperty(workflowId)) {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  ...tempWorkflowBuilderErrors.actions[workflowId],
                  "Please select a Date to base data changes on",
                ];
              } else {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  "Please select a Date to base data changes on",
                ];
              }
            }

            // Exclude holidays choice required:
            if (!action.hasOwnProperty("bExcludeHolidays")) {
              if (tempWorkflowBuilderErrors.actions.hasOwnProperty(workflowId)) {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  ...tempWorkflowBuilderErrors.actions[workflowId],
                  "Please provide if you want to Exclude holidays",
                ];
              } else {
                tempWorkflowBuilderErrors.actions[workflowId] = [
                  "Please provide if you want to Exclude holidays",
                ];
              }
            }
          }
        }
      });
    }

    // Approvals Error validation:
    if (referenceData && referenceData.approvals && Array.isArray(referenceData.approvals)) {
      referenceData.approvals.map((approval: any) => {
        const workflowId = approval.workflowApprovalsID;

        // Optional message:

        // One or more decion outcomes:
        // (Check order numbers and names exist for all)
        const approvalOutcomeIds = approval.childrenWorkflowApprovalOutcomeIDs;

        if (Array.isArray(approvalOutcomeIds) && approvalOutcomeIds.length > 0) {
          approvalOutcomeIds.forEach((outcomeId) => {
            if (referenceData.approvalOutcomes && Array.isArray(referenceData.approvalOutcomes)) {
              const match = referenceData.approvalOutcomes.filter(
                (item: any) => item.workflowApprovalOutcomesID === outcomeId
              );

              if (Array.isArray(match) && match.length === 1 && typeof match[0] === "object") {
                const tempData = match[0];

                let nameValid = true;
                if (typeof tempData.workflowApprovalOutcomesName === "string") {
                  if (tempData.workflowApprovalOutcomesName.trim() === "") {
                    nameValid = false;
                  }
                } else {
                  nameValid = false;
                }

                if (!nameValid) {
                  if (tempWorkflowBuilderErrors.approvals.hasOwnProperty(workflowId)) {
                    tempWorkflowBuilderErrors.approvals[workflowId] = [
                      ...tempWorkflowBuilderErrors.approvals[workflowId],
                      "Please provide a name for each Decision outcome",
                    ];
                  } else {
                    tempWorkflowBuilderErrors.approvals[workflowId] = [
                      "Please provide a name for each Decision outcome",
                    ];
                  }
                }

                // Check all outcomes contain an action or an approval:
                if (!tempData.childWorkflowActionID && !tempData.childWorkflowApprovalID) {
                  const approvalOutcomeId = tempData.workflowApprovalOutcomesID;

                  if (
                    tempWorkflowBuilderErrors.approvalOutcomes.hasOwnProperty(approvalOutcomeId)
                  ) {
                    tempWorkflowBuilderErrors.approvalOutcomes[approvalOutcomeId] = [
                      ...tempWorkflowBuilderErrors.approvalOutcomes[approvalOutcomeId],
                      "Please provide at least one action or approval for each approval outcome",
                    ];
                  } else {
                    tempWorkflowBuilderErrors.approvalOutcomes[approvalOutcomeId] = [
                      "Please provide at least one action or approval for each approval outcome",
                    ];
                  }
                }
              }
            }
          });
        } else {
          if (tempWorkflowBuilderErrors.approvals.hasOwnProperty(workflowId)) {
            tempWorkflowBuilderErrors.approvals[workflowId] = [
              ...tempWorkflowBuilderErrors.approvals[workflowId],
              "Please provide at least one Decision outcome",
            ];
          } else {
            tempWorkflowBuilderErrors.approvals[workflowId] = [
              "Please provide at least one Decision outcome",
            ];
          }
        }

        // One or more contact types and/or one user pool required:

        let contactTypeValid = true;
        let userpoolValid = true;

        if (
          !approval.approvalContactTypesIDs ||
          (approval.approvalContactTypesIDs && approval.approvalContactTypesIDs.length < 1)
        ) {
          contactTypeValid = false;
        }

        if (!approval.approvalUserPoolsID) {
          userpoolValid = false;
        }

        if (!contactTypeValid && !userpoolValid) {
          if (tempWorkflowBuilderErrors.approvals.hasOwnProperty(workflowId)) {
            tempWorkflowBuilderErrors.approvals[workflowId] = [
              ...tempWorkflowBuilderErrors.approvals[workflowId],
              "Please select one or more contact types and/or one user pool",
            ];
          } else {
            tempWorkflowBuilderErrors.approvals[workflowId] = [
              "Please select one or more contact types and/or one user pool",
            ];
          }
        }

        // bReasonCode:
      });
    }

    let tempFormValid = true;

    Object.keys(tempWorkflowBuilderErrors).forEach((key) => {
      const value: any = tempWorkflowBuilderErrors[key as keyof typeof tempWorkflowBuilderErrors];

      if (typeof value === "object") {
        if (Object.keys(value).length > 0) {
          tempFormValid = false;
        }
      } else if (Array.isArray(value) && value.length > 0) {
        tempFormValid = false;
      }
    });

    setWorkflowBuilderErrors(tempWorkflowBuilderErrors);

    setValidating(false);

    setFormValid(tempFormValid);
  };

  const clearIdsForNewlyCreatedData = (
    data: any[],
    fieldToCheck: string,
    idsToCheck: number[],
    fieldToClear: string,
    fieldValuesAreAnArray: boolean = false
  ) => {
    let parsedData: any[] = [];

    data.forEach((item) => {
      if (fieldValuesAreAnArray) {
        if (
          typeof item === "object" &&
          item.hasOwnProperty(fieldToCheck) &&
          item.hasOwnProperty(fieldToClear)
        ) {
          let tempItem = _.cloneDeep(item);

          let tempArray = [...item[fieldToClear]];

          tempArray = tempArray.filter((item: any) => !idsToCheck.includes(item));

          if (tempArray.length > 0) {
            tempItem[fieldToClear] = tempArray;
          } else {
            tempItem[fieldToClear] = null;
          }

          parsedData.push(tempItem);
        } else {
          parsedData.push(item);
        }
      } else {
        if (
          typeof item === "object" &&
          item.hasOwnProperty(fieldToCheck) &&
          idsToCheck.includes(item[fieldToCheck]) &&
          item.hasOwnProperty(fieldToClear)
        ) {
          let tempItem = _.cloneDeep(item);

          tempItem[fieldToClear] = null;

          parsedData.push(tempItem);
        } else {
          parsedData.push(item);
        }
      }
    });
    return parsedData;
  };

  const getIdsForNewlyCreatedData = (data: any[], idFieldName: string) => {
    let ids: any[] = [];

    data.forEach((item) => {
      if (
        typeof item === "object" &&
        item.hasOwnProperty("bNewlyCreated") &&
        item.bNewlyCreated === true &&
        item.hasOwnProperty(idFieldName)
      ) {
        ids.push(item[idFieldName]);
      }
    });
    return ids;
  };

  const submitData = async () => {
    // Parse data for payload:

    setSubmitting(true);

    const newlyCreatedActionIds =
      referenceData && referenceData.actions && Array.isArray(referenceData.actions)
        ? getIdsForNewlyCreatedData(referenceData.actions, "workflowActionsID")
        : null;

    const newlyCreatedApprovalIds =
      referenceData && referenceData.approvals && Array.isArray(referenceData.approvals)
        ? getIdsForNewlyCreatedData(referenceData.approvals, "workflowApprovalsID")
        : null;

    const newlyCreatedApprovalOutcomeIds =
      referenceData &&
      referenceData.approvalOutcomes &&
      Array.isArray(referenceData.approvalOutcomes)
        ? getIdsForNewlyCreatedData(referenceData.approvalOutcomes, "workflowApprovalOutcomesID")
        : null;

    // Actions:
    let parsedActions: any = referenceData?.actions;

    if (referenceData && referenceData.actions && Array.isArray(referenceData.actions)) {
      if (newlyCreatedActionIds) {
        parsedActions = clearIdsForNewlyCreatedData(
          parsedActions,
          "workflowActionsID",
          newlyCreatedActionIds,
          "workflowActionsID"
        );
        parsedActions = clearIdsForNewlyCreatedData(
          parsedActions,
          "childWorkflowActionID",
          newlyCreatedActionIds,
          "childWorkflowActionID"
        );
      }

      if (newlyCreatedApprovalIds) {
        parsedActions = clearIdsForNewlyCreatedData(
          parsedActions,
          "childWorkflowApprovalID",
          newlyCreatedApprovalIds,
          "childWorkflowApprovalID"
        );
      }
    }

    // Approvals:
    let parsedApprovals: any = referenceData?.approvals;

    if (referenceData && referenceData.approvals && Array.isArray(referenceData.approvals)) {
      if (newlyCreatedApprovalIds) {
        parsedApprovals = clearIdsForNewlyCreatedData(
          parsedApprovals,
          "workflowApprovalsID",
          newlyCreatedApprovalIds,
          "workflowApprovalsID"
        );
      }

      if (newlyCreatedApprovalOutcomeIds) {
        parsedApprovals = clearIdsForNewlyCreatedData(
          parsedApprovals,
          "childrenWorkflowApprovalOutcomeIDs",
          newlyCreatedApprovalOutcomeIds,
          "childrenWorkflowApprovalOutcomeIDs",
          true
        );
      }
    }

    // Approval Outcomes:

    let parsedApprovalOutcomes: any = referenceData?.approvalOutcomes;

    if (
      referenceData &&
      referenceData.approvalOutcomes &&
      Array.isArray(referenceData.approvalOutcomes)
    ) {
      if (newlyCreatedApprovalOutcomeIds) {
        parsedApprovalOutcomes = clearIdsForNewlyCreatedData(
          parsedApprovalOutcomes,
          "workflowApprovalOutcomesID",
          newlyCreatedApprovalOutcomeIds,
          "workflowApprovalOutcomesID"
        );
      }

      if (newlyCreatedActionIds) {
        parsedApprovalOutcomes = clearIdsForNewlyCreatedData(
          parsedApprovalOutcomes,
          "childWorkflowActionID",
          newlyCreatedActionIds,
          "childWorkflowActionID"
        );
      }
      if (newlyCreatedApprovalIds) {
        parsedApprovalOutcomes = clearIdsForNewlyCreatedData(
          parsedApprovalOutcomes,
          "childWorkflowApprovalID",
          newlyCreatedApprovalIds,
          "childWorkflowApprovalID"
        );
      }
    }

    // Remove bNewlyAdded keys:
    parsedActions = parsedActions
      ? parsedActions.map((item: any) => {
          const newObj = _.cloneDeep(item);

          if (newObj.hasOwnProperty("bNewlyCreated")) {
            delete newObj["bNewlyCreated"];
          }

          return newObj;
        })
      : null;

    parsedApprovals = parsedApprovals
      ? parsedApprovals.map((item: any) => {
          const newObj = _.cloneDeep(item);

          if (newObj.hasOwnProperty("bNewlyCreated")) {
            delete newObj["bNewlyCreated"];
          }

          if (newObj.hasOwnProperty("approvalContactTypesIDs")) {
            newObj.contactTypesIDs = newObj.approvalContactTypesIDs;
            delete newObj["approvalContactTypesIDs"];
          }

          if (newObj.hasOwnProperty("approvalUserPoolsID")) {
            newObj.userPoolsID = newObj.approvalUserPoolsID;
            delete newObj["approvalUserPoolsID"];
          }

          return newObj;
        })
      : null;

    parsedApprovalOutcomes = parsedApprovalOutcomes
      ? parsedApprovalOutcomes.map((item: any) => {
          const newObj = _.cloneDeep(item);

          if (newObj.hasOwnProperty("bNewlyCreated")) {
            delete newObj["bNewlyCreated"];
          }

          return newObj;
        })
      : null;

    let parsedTrigger = referenceData && referenceData.trigger ? referenceData.trigger : null;

    if (parsedTrigger && parsedTrigger.hasOwnProperty("bNewlyCreated")) {
      delete parsedTrigger["bNewlyCreated"];

      parsedTrigger.workflowTriggersID = null;
    }

    if (parsedTrigger && parsedTrigger.hasOwnProperty("bApplyToAllProjects")) {
      delete parsedTrigger["bApplyToAllProjects"];
    }

    let payload: any = {
      workflow: {
        workflowsID: workflowsId,

        workflowTrigger: parsedTrigger,

        workflowActions: parsedActions,
        workflowApprovals: parsedApprovals,
        workflowApprovalOutcomes: parsedApprovalOutcomes,

        templateGateways: templateGateways,
        allTasks: allTasksOriginalData,
        workflowTriggerTypes: workflowTriggerTypes,
        documentTypes: documentTypes,
        projectTypes: projectTypes,
        userPools: userPools,
        contactTypes: contactTypes,
        workflowActionTypes: workflowActionTypes,
      },
    };

    try {
      const res = await api.post(endpoints.workflowConfiguration.builder, payload ?? undefined);
      if (res.status === 200 && res.data.status === "1") {
        onDone();
      } else {
        toast(
          <ToastMessage
            toastData={[{ title: "Error", text: "Failed to save workflow" }]}
            className="ToastError"
          />,
          {
            ...toastConfig,
          }
        );

        setFormValid(false);
      }
    } catch (err) {
      setFormValid(false);
      setSubmitting(false);
      console.log(err);
      const error: any = err;
      if (
        error.response &&
        error.response.data &&
        error.response.data.errors &&
        Array.isArray(error.response.data.errors) &&
        error.response.data.errors.length > 0
      ) {
        let tempErrors: ErrorResponse[] = [];

        error.response.data.errors.forEach((item: any) => {
          if (typeof item === "object") {
            let fieldName: string | null = null;
            let messageCode: string | null = null;

            if (item.fieldName) {
              fieldName = item.fieldName;
            }
            if (item.messageCode) {
              messageCode = item.messageCode;
            }

            if (fieldName && messageCode) {
              const parsedMessageCode = capitaliseStringFirstLetter(
                messageCode.toLowerCase().replace(/_/g, " ")
              ).trim();

              tempErrors = [
                ...tempErrors,
                {
                  fieldName,
                  messageText: parsedMessageCode,
                },
              ];
            }
          }
        });

        const unparsedErrors = error.response.data.errors;

        let parsedErrors: any = {};
        unparsedErrors.forEach((item: any) => {
          if (
            typeof item === "object" &&
            item.hasOwnProperty("fieldName") &&
            item.hasOwnProperty("messageCode")
          ) {
            const parsedFieldName = parseCamelCase(item.fieldName)
              .split("_")
              .join(" ")
              .replace(/_/g, " ")
              .replaceAll(/  +/g, " ")
              .trim();
            const parsedMessageCode = item.messageCode
              .split("_")
              .join(" ")
              .replace(/_/g, " ")
              .replaceAll(/  +/g, " ")
              .trim();
            parsedErrors[parsedFieldName] = parsedMessageCode;
          }
        });

        if (Object.keys(parsedErrors).length > 0) {
          dispatch(setSubmitResponseErrors({ submitResponseErrors: parsedErrors }));
        }
      }
    }

    setSubmitting(false);
  };

  useEffect(() => {
    if (formValid) {
      submitData();
    }
  }, [formValid]);

  const submitResponseErrors = useSelector(selectSubmitResponse).submitResponseErrors;

  const dispatch = useDispatch();

  useEffect(() => {
    if (
      submitResponseErrors &&
      Object.keys(submitResponseErrors).length > 0 &&
      parseSubmitResponseEndpointErrors(submitResponseErrors)
    ) {
      setParsedSubmitResponseErrors(parseSubmitResponseEndpointErrors(submitResponseErrors));

      dispatch(clearSubmitResponseErrors());
    }
  }, [submitResponseErrors]);

  useEffect(() => {
    if (parsedSubmitResponseErrors) {
      toast(<ToastMessage toastData={parsedSubmitResponseErrors} className="ToastError" />, {
        ...toastConfig,
      });
    }
  }, [parsedSubmitResponseErrors]);

  const [showAddPage, setShowAddPage] = useState<boolean>(false);
  const [addPageCurrentPage, setAddPageCurrentPage] = useState<AddPages>(
    // AddPages.Data
    AddPages.Milestone
  );
  const [editPageToggle, setEditPageToggle] = useState<boolean>(false);
  const [addPageActionType, setAddPageActionType] = useState<AddPageActionType>(actionType);
  const [taskId, setTaskId] = useState<number | null>(null);
  const [gatewayId, setGatewayId] = useState<number | null>(null);

  const [unParsedTasksAndMilestones, setUnParsedTasksAndMilestones] =
    useState<UnParsedTasksAndMilestones | null>(null);

  const [reloadGatewayDataToggle, setReloadGatewayDataToggle] = useState<boolean>(false);

  useEffect(() => {
    fetchBuilderInfo(true);
  }, [reloadGatewayDataToggle]);

  const refreshWorkflowBuilderData = () => {
    setReloadGatewayDataToggle(!reloadGatewayDataToggle);
    setShowAddPage(false);
  };

  const MAX_ADD_PAGES = 2;

  const nextPage = () => {
    if (currentPageNumber < MAX_ADD_PAGES) {
      setAddPageCurrentPage(addPageCurrentPage + 1);
    }
  };

  return (
    <div className="WorkflowBuilderPage">
      <div className="WorkflowHeader">
        <div className="Title">Workflow builder</div>

        <div className="Info"></div>

        <div className="Section">
          <div className="Text">Section 2 of 3</div>
        </div>
      </div>

      {loading && !getEndpointError && <Spinner />}

      {!loading && !getEndpointError && initialValues && (
        <DragDropContext onDragEnd={(result) => onDragEnd(result)}>
          <div className="PageWrapper">
            <div className={`PageContainer ${taskPickerMimised ? "TaskPickerMinimised" : ""}`}>
              <div className={`MaximiseIconContainer ${!taskPickerMimised ? "Disabled" : ""}`}>
                <img
                  src={FilterIcon}
                  alt="Maximise milestones and tasks panel"
                  className="MaximiseIcon"
                  onClick={() => setTaskPickerMimised(false)}
                />
              </div>

              <div className={`NodeTreeContainer ${originalActionType}`}>
                {/* Initial workflow item */}

                {parsedData ? (
                  <WorkflowItem
                    key={`workflow-item-${0}`}
                    parentOrderNumbers="0"
                    uniqueId={"0"}
                    parentOrderNumber={parsedData.parentOrderNumber}
                    orderNumber={parsedData.orderNumber}
                    childElements={parsedData.childElements}
                    triggerOptions={workflowTriggerTypes}
                    documentOptions={documentTypes}
                    projectOptions={projectTypes}
                    triggerDatesOptions={triggerDatesOptions}
                    parsedData={parsedData}
                    setParsedData={setParsedData}
                    itemData={parsedData}
                    referenceData={referenceData}
                    setReferenceData={setReferenceData}
                    contactTypes={contactTypes}
                    userPools={userPools}
                    actionTypes={workflowActionTypes}
                    parentChildElementsLength={1}
                    tempTriggerMilestoneData={tempTriggerMilestoneData}
                    setTempTriggerMilestoneData={setTempTriggerMilestoneData}
                    tempTriggerMilestoneId={tempTriggerMilestoneId}
                    setTempTriggerMilestoneId={setTempTriggerMilestoneId}
                    tempActionMilestoneId={tempActionMilestoneId}
                    setTempActionMilestoneId={setTempActionMilestoneId}
                    tempActionMilestoneData={tempActionMilestoneData}
                    setTempActionMilestoneData={setTempActionMilestoneData}
                    tempActionDestinationMilestoneId={tempActionDestinationMilestoneId}
                    setTempActionDestinationMilestoneId={setTempActionDestinationMilestoneId}
                    milestonestasksList={milestonestasksList}
                    setMilestonestasksList={setMilestonestasksList}
                    workflowBuilderErrors={workflowBuilderErrors}
                    setWorkflowBuilderErrors={setWorkflowBuilderErrors}
                    childElementIndex={0}
                    totalNumberofChildElements={1}
                    unParsedTasksAndMilestones={unParsedTasksAndMilestones}
                    workflows={workflows}
                    surveyTemplateOptions={surveyTemplateOptions}
                  />
                ) : (
                  <div className="NoData">No workflow data</div>
                )}
              </div>

              {workflowsId && templateGateways && (
                <MilestoneTaskPicker
                  workflowId={workflowsId}
                  templateGatewaysList={templateGateways}
                  tempTriggerMilestoneData={tempTriggerMilestoneData}
                  setTempTriggerMilestoneData={setTempTriggerMilestoneData}
                  tempTriggerMilestoneId={tempTriggerMilestoneId}
                  setTempTriggerMilestoneId={setTempTriggerMilestoneId}
                  tempActionMilestoneId={tempActionMilestoneId}
                  setTempActionMilestoneId={setTempActionMilestoneId}
                  tempActionMilestoneData={tempActionMilestoneData}
                  setTempActionMilestoneData={setTempActionMilestoneData}
                  taskPickerMimised={taskPickerMimised}
                  setTaskPickerMimised={setTaskPickerMimised}
                  setShowAddPage={setShowAddPage}
                  setGatewayId={setGatewayId}
                  milestonestasksList={milestonestasksList}
                  setMilestonestasksList={setMilestonestasksList}
                  setUnParsedTasksAndMilestones={setUnParsedTasksAndMilestones}
                />
              )}
            </div>

            <ProgressBar currentPage={2} totalPages={3} />
            <div className="Buttons">
              <div className="Btn BtnCancel" onClick={closePanel}>
                Cancel
              </div>
              <button
                className={`Btn SubmitBtn ${validating || submitting ? "Disabled" : ""}`}
                type="submit"
                onClick={startSubmit}
              >
                {`${submitButtonText}`}
              </button>
            </div>
          </div>
        </DragDropContext>
      )}

      {getEndpointError && (
        <div className="EndpointError">
          <div className="Text">Something went wrong please try again later.</div>

          <div className="Buttons">
            <div className="Btn BtnCancel" onClick={closePanel}>
              Cancel
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowBuilderPage;
